
/**
 * Module dependencies.
 */

import { MediaProps, ProductProps } from 'src/types/products';
import { absoluteUrlResolver } from 'src/core/utils/url-resolver';
import { media, units } from 'src/styles/utils';
import { mediasProductName } from 'src/core/collections-config/media';
import { resolvePageUrl, routeTemplate } from 'src/core/utils/routes';
import { routes } from 'src/core/routes';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import DownloadsSection, {
  Item as DownloadItem
} from 'src/components/sections/downloads-section';

import FavoritesProvider from 'src/context/favorites/provider';
import Header from 'src/components/products/product-details/header';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { ReactElement, useMemo } from 'react';
import RelatedGallery from 'src/components/sections/related-gallery';
import SpecsSection from 'src/components/products/product-details/specs-section';
import ToolsSection from 'src/components/sections/tools-section';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import simulatorIcon from 'src/assets/svg/simulator.svg';
import styled from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import useProduct from 'src/api/app/products/use-product';

/**
 * Default locale.
 */

const defaultLocale: string = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;

/**
 * Export `Props` type.
 */

export type Props = {
  isKiosk?: boolean,
  locale: string,
  product: ProductProps | null
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  min-height: 100vh;
  position: relative;
`;

/**
 * `StyledToolsSection` styled component.
 */

const StyledToolsSection = styled(ToolsSection)`
  padding: ${units(13)} 0;

  ${media.min('md')`
    padding: ${units(25)} 0;
  `};
`;

/**
 * Get image by collection name.
 */

function getImageByCollectionName(medias: MediaProps[], collectionName: string): string | null {
  const media = head(filter(medias, {
    collectionName: mediasProductName[collectionName]
  }));

  return media?.fileUrl;
}

/**
 * `ProductPage` component.
 */

const ProductPage = (props: Props): ReactElement => {
  const { isKiosk, locale } = props;
  const { locales, query } = useRouter();
  const { translate } = useTranslate();
  const {
    data,
    isLoading,
    isSuccess
  } = useProduct<ProductProps>({
    initialData: props.product,
    slug: get(query, 'slug')
  });

  const productTitle = `${data?.name} ${data?.format?.name}`;
  const productDescription = translate('products:descriptionSeo', { productTitle });
  const medias = useMemo(() => data?.media ?? [], [data?.media]);
  const technicalInformation: DownloadItem[] = useMemo(() => [{
    downloadUrl: data?.technicalInformationDownload?.downloadUrl,
    type: 'technicalInformation'
  }, {
    downloadUrl: data?.performanceStatementDownload?.downloadUrl,
    type: 'performanceStatement'
  }, {
    downloadUrl: getImageByCollectionName(medias, 'imagesArchive'),
    type: 'images'
  }, {
    downloadUrl: data?.epdDownload?.downloadUrl,
    type: 'epd'
  }, {
    downloadUrl: data?.upecDownload?.downloadUrl,
    type: 'upec'
  }], [data, medias]);

  const hrefLangs = useMemo(() => {
    return locales.map(locale => {
      const route = routeTemplate(routes[locale].productDetails, { slug: data?.slug });

      return {
        lang: locale,
        url: defaultLocale === locale ? absoluteUrlResolver(`${route}`) : absoluteUrlResolver(`/${locale}${route}`)
      };
    });
  }, [data?.slug, locales]);

  useNavbarTheme({ theme: 'dark' });

  const canonicalUrl = `${resolvePageUrl('collections', locale)}/${data.productCollectionSlug}`;

  return (
    <>
      <Metatags
        canonicalUrl={canonicalUrl}
        description={data?.descriptionSeo || productDescription}
        hrefLangs={hrefLangs}
        isKiosk={isKiosk}
        pageTitle={productTitle}
        title={data?.titleSeo}
      />

      <Wrapper>
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <FavoritesProvider>
              <Header
                {...pick(data, [
                  'descriptionSeo',
                  'finish',
                  'format',
                  'function',
                  'id',
                  'media',
                  'name',
                  'productCollectionSlug',
                  'slug',
                  'typology',
                  'videoUrl'
                ])}
                isKiosk={isKiosk}
              />

              <SpecsSection
                isKiosk={isKiosk}
                {...data}
              />

              {!isKiosk && data?.simulator && (
                <StyledToolsSection
                  description={translate('common:sections.tools.description')}
                  isKiosk={isKiosk}
                  items={[{
                    buttonLabel: translate('common:sections.tools.items.simulator.buttonLabel'),
                    buttonUrl: data?.simulatorUrl,
                    description: translate('common:sections.tools.items.simulator.description'),
                    icon: simulatorIcon,
                    iconType: 'svg',
                    title: translate('common:sections.tools.items.simulator.title')
                  }]}
                  title={translate('common:sections.tools.title')}
                />
              )}

              {!isKiosk && (
                <DownloadsSection
                  description={translate('common:sections.technicalInformation.description')}
                  items={filter(technicalInformation, ({ downloadUrl }) => !isEmpty(downloadUrl))}
                  title={translate('common:sections.technicalInformation.title')}
                />
              )}

              <RelatedGallery
                isKiosk={isKiosk}
                items={data?.relatedProducts as ProductProps[]}
                label={translate('products:labels.related')}
                type={'product'}
              />
            </FavoritesProvider>
          </>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `ProductPage` component.
 */

export default ProductPage;
